import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ActivityIcon,
  UsersIcon,
  DatabaseIcon,
  CpuIcon,
  ZapIcon,
  RefreshIcon,
  AlertIcon,
  CheckCircleIcon,
  BuildingIcon,
  ArrowUpIcon,
  ArrowDownIcon
} from '../components/common/Icons';

// ============================================================================
// Types (match API response schemas from platform-api/app/schemas/usage.py)
// ============================================================================

interface TenantUsageSummaryItem {
  tenant_id: string;
  tenant_name: string;
  tenant_slug: string;
  status: string;
  api_requests_today: number;
  api_requests_month: number;
  tokens_month: number;
  storage_total_bytes: number;
  total_users: number;
  active_users_today: number;
  api_usage_percent: number | null;
  token_usage_percent: number | null;
  storage_usage_percent: number | null;
  limits_ok: boolean;
}

interface UsageSummaryResponse {
  total_tenants: number;
  active_tenants: number;
  total_api_requests_today: number;
  total_tokens_today: number;
  total_active_users_today: number;
  total_api_requests_month: number;
  total_tokens_month: number;
  total_storage_bytes: number;
  tenants_over_api_limit: number;
  tenants_over_token_limit: number;
  tenants_over_storage_limit: number;
  tenants: TenantUsageSummaryItem[];
  generated_at: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/** Format bytes to human readable (KB, MB, GB, TB) */
function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/** Format large numbers with K, M, B suffixes */
function formatNumber(num: number): string {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + 'B';
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + 'M';
  if (num >= 1_000) return (num / 1_000).toFixed(1) + 'K';
  return num.toString();
}

// ============================================================================
// Stat Card Component
// ============================================================================

interface StatCardProps {
  label: string;
  value: string;
  subValue?: string;
  icon: React.ReactNode;
  alert?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, subValue, icon, alert }) => (
  <div className={`stat-card ${alert ? 'stat-card-alert' : ''}`}>
    <div className="stat-card-icon">{icon}</div>
    <div className="stat-card-content">
      <div className="stat-card-label">{label}</div>
      <div className="stat-card-value">{value}</div>
      {subValue && <div className="stat-card-subvalue">{subValue}</div>}
    </div>
  </div>
);

// ============================================================================
// Sorting
// ============================================================================

type SortKey = 'tenant_name' | 'status' | 'api_requests_today' | 'tokens_month' | 'storage_total_bytes' | 'total_users' | 'limits_ok';
type SortDir = 'asc' | 'desc';

const COLUMN_HEADERS: { key: SortKey; label: string }[] = [
  { key: 'tenant_name', label: 'Tenant' },
  { key: 'status', label: 'Status' },
  { key: 'api_requests_today', label: 'API Today' },
  { key: 'tokens_month', label: 'Tokens (Month)' },
  { key: 'storage_total_bytes', label: 'Storage' },
  { key: 'total_users', label: 'Users' },
  { key: 'limits_ok', label: 'Limits' },
];

function compareTenants(a: TenantUsageSummaryItem, b: TenantUsageSummaryItem, key: SortKey, dir: SortDir): number {
  let cmp = 0;
  switch (key) {
    case 'tenant_name':
      cmp = a.tenant_name.localeCompare(b.tenant_name);
      break;
    case 'status':
      cmp = a.status.localeCompare(b.status);
      break;
    case 'limits_ok':
      cmp = (a.limits_ok === b.limits_ok) ? 0 : a.limits_ok ? 1 : -1;
      break;
    default:
      cmp = (a[key] as number) - (b[key] as number);
  }
  return dir === 'asc' ? cmp : -cmp;
}

// ============================================================================
// Sortable Header
// ============================================================================

interface SortableThProps {
  label: string;
  sortKey: SortKey;
  currentKey: SortKey;
  currentDir: SortDir;
  onSort: (key: SortKey) => void;
}

const SortableTh: React.FC<SortableThProps> = ({ label, sortKey, currentKey, currentDir, onSort }) => {
  const active = currentKey === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        {active ? (
          currentDir === 'asc' ? <ArrowUpIcon size={14} /> : <ArrowDownIcon size={14} />
        ) : (
          <span style={{ opacity: 0.3, display: 'inline-flex' }}><ArrowUpIcon size={14} /></span>
        )}
      </span>
    </th>
  );
};

// ============================================================================
// Tenant Usage Table Component
// ============================================================================

interface TenantUsageTableProps {
  tenants: TenantUsageSummaryItem[];
  onSelectTenant?: (tenantId: string) => void;
}

const TenantUsageTable: React.FC<TenantUsageTableProps> = ({ tenants, onSelectTenant }) => {
  const [sortKey, setSortKey] = useState<SortKey>('tenant_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = useCallback((key: SortKey) => {
    setSortKey(prev => {
      if (prev === key) {
        setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        return key;
      }
      setSortDir('asc');
      return key;
    });
  }, []);

  const sorted = useMemo(
    () => [...tenants].sort((a, b) => compareTenants(a, b, sortKey, sortDir)),
    [tenants, sortKey, sortDir]
  );

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            {COLUMN_HEADERS.map(col => (
              <SortableTh
                key={col.key}
                label={col.label}
                sortKey={col.key}
                currentKey={sortKey}
                currentDir={sortDir}
                onSort={handleSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((tenant) => (
            <tr
              key={tenant.tenant_id}
              className={onSelectTenant ? 'clickable-row' : ''}
              onClick={() => onSelectTenant?.(tenant.tenant_id)}
            >
              <td>
                <div className="tenant-cell">
                  <BuildingIcon size={16} />
                  <div>
                    <div className="tenant-name">{tenant.tenant_name}</div>
                    <div className="tenant-slug">{tenant.tenant_slug}</div>
                  </div>
                </div>
              </td>
              <td>
                <span className={`status-badge status-${tenant.status}`}>
                  {tenant.status}
                </span>
              </td>
              <td>{formatNumber(tenant.api_requests_today)}</td>
              <td>{formatNumber(tenant.tokens_month)}</td>
              <td>{formatBytes(tenant.storage_total_bytes)}</td>
              <td>
                <span title={`${tenant.active_users_today} active today`}>
                  {tenant.total_users} ({tenant.active_users_today})
                </span>
              </td>
              <td>
                {tenant.limits_ok ? (
                  <span className="limit-ok"><CheckCircleIcon size={16} /> OK</span>
                ) : (
                  <span className="limit-exceeded"><AlertIcon size={16} /> Exceeded</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const UsageDashboardPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<UsageSummaryResponse | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  const fetchUsageSummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/admin/usage/summary', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || sessionStorage.getItem('access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch usage data: ${response.statusText}`);
      }

      const data: UsageSummaryResponse = await response.json();
      setSummary(data);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch usage data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsageSummary();
  }, [fetchUsageSummary]);

  // Count of tenants with issues
  const tenantsWithIssues = summary
    ? summary.tenants_over_api_limit + summary.tenants_over_token_limit + summary.tenants_over_storage_limit
    : 0;

  return (
    <div className="page-container usage-dashboard-page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <h1><ActivityIcon size={24} /> Platform Usage</h1>
          <p className="page-description">
            Monitor platform-wide resource usage and tenant metrics
          </p>
        </div>
        <div className="page-header-actions" style={{ gap: '12px' }}>
          <span className="last-refresh">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            className="btn btn-secondary"
            onClick={fetchUsageSummary}
            disabled={loading}
          >
            <RefreshIcon size={16} /> Refresh
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={18} />
          <span>{error}</span>
          <button className="btn btn-sm" onClick={fetchUsageSummary}>Retry</button>
        </div>
      )}

      {/* Loading State */}
      {loading && !summary && (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading usage metrics...</p>
        </div>
      )}

      {/* Content */}
      {summary && (
        <>
          {/* Platform Overview Cards */}
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ marginBottom: '16px' }}>Platform Overview</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
              <StatCard
                label="Active Tenants"
                value={summary.active_tenants.toString()}
                subValue={`of ${summary.total_tenants} total`}
                icon={<BuildingIcon size={24} />}
              />
              <StatCard
                label="API Requests Today"
                value={formatNumber(summary.total_api_requests_today)}
                subValue={`${formatNumber(summary.total_api_requests_month)} this month`}
                icon={<ZapIcon size={24} />}
              />
              <StatCard
                label="Tokens Today"
                value={formatNumber(summary.total_tokens_today)}
                subValue={`${formatNumber(summary.total_tokens_month)} this month`}
                icon={<CpuIcon size={24} />}
              />
              <StatCard
                label="Total Storage"
                value={formatBytes(summary.total_storage_bytes)}
                icon={<DatabaseIcon size={24} />}
              />
            </div>
          </section>

          {/* Active Users Card */}
          <section style={{ marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
              <StatCard
                label="Active Users Today"
                value={summary.total_active_users_today.toString()}
                icon={<UsersIcon size={24} />}
              />
            </div>
          </section>

          {/* Alerts Section - Only show if there are issues */}
          {tenantsWithIssues > 0 && (
            <section style={{ marginBottom: '32px' }}>
              <h2 style={{ marginBottom: '16px' }}><AlertIcon size={20} /> Limit Alerts</h2>
              <div className="alert-cards">
                {summary.tenants_over_api_limit > 0 && (
                  <div className="alert-card alert-card-warning">
                    <div className="alert-card-value">{summary.tenants_over_api_limit}</div>
                    <div className="alert-card-label">Tenants over API limit</div>
                  </div>
                )}
                {summary.tenants_over_token_limit > 0 && (
                  <div className="alert-card alert-card-warning">
                    <div className="alert-card-value">{summary.tenants_over_token_limit}</div>
                    <div className="alert-card-label">Tenants over token limit</div>
                  </div>
                )}
                {summary.tenants_over_storage_limit > 0 && (
                  <div className="alert-card alert-card-error">
                    <div className="alert-card-value">{summary.tenants_over_storage_limit}</div>
                    <div className="alert-card-label">Tenants over storage limit</div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Per-Tenant Usage Table */}
          <section style={{ marginTop: '8px' }}>
            <h2 style={{ marginBottom: '16px' }}>Tenant Usage</h2>
            {summary.tenants.length > 0 ? (
              <TenantUsageTable tenants={summary.tenants} />
            ) : (
              <div className="empty-state">
                <BuildingIcon size={48} />
                <p>No tenants found</p>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default UsageDashboardPage;
